import type { PaginatedResponse } from '@/types'
import type { ActionPermissions } from '@/lib/permissions'

export type PaginatedWithPermissions<T> = PaginatedResponse<T> & {
  action_permissions?: ActionPermissions
}

export type ListWithPermissions<T> = {
  results: T[]
  action_permissions?: ActionPermissions
}

/** Normalize paginated API payloads and preserve ``action_permissions``. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toPaginated<T>(data: any): PaginatedWithPermissions<T> {
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data }
  }
  if (data?.results) {
    return {
      count: data.count,
      next: data.next ?? null,
      previous: data.previous ?? null,
      results: data.results,
      action_permissions: data.action_permissions,
    }
  }
  if (data?.data && Array.isArray(data.data)) {
    return {
      count: data.count ?? data.data.length,
      next: data.next ?? null,
      previous: data.previous ?? null,
      results: data.data,
      action_permissions: data.action_permissions,
    }
  }
  return { count: 0, next: null, previous: null, results: [] }
}

/** Unwrap a list endpoint that may return a bare array or paginated object. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapList<T>(data: any): ListWithPermissions<T> {
  if (Array.isArray(data)) {
    return { results: data }
  }
  const results = (data?.results ?? data?.data ?? []) as T[]
  return {
    results,
    action_permissions: data?.action_permissions,
  }
}

/** Largest ``page_size`` the backend's AuthPageNumberPagination honours. */
export const MAX_API_PAGE_SIZE = 100

/**
 * Walk every page of a DRF page-number list and return one merged payload.
 *
 * For pickers/dropdowns that must offer the COMPLETE list — a single request
 * is capped at ``MAX_API_PAGE_SIZE`` (default page is only 10), so without
 * this anything past the first page silently disappears. ``fetchPage`` is
 * called with 1, 2, … until the response has no ``next`` link (bare-array
 * responses stop after the first call). ``maxPages`` is a runaway guard.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<PaginatedWithPermissions<T>>,
  { pageSize = MAX_API_PAGE_SIZE, maxPages = 50 }: { pageSize?: number; maxPages?: number } = {},
): Promise<PaginatedWithPermissions<T>> {
  const first = await fetchPage(1, pageSize)
  const results = [...first.results]
  let next = first.next
  for (let page = 2; next && page <= maxPages; page += 1) {
    const resp = await fetchPage(page, pageSize)
    results.push(...resp.results)
    next = resp.next
  }
  return { ...first, count: results.length, next: null, previous: null, results }
}
